import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ArrowLeft, CheckCircle2, XCircle, Lightbulb, Printer, FileText } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from '../hooks/use-toast';
import { lessonAPI, progressAPI } from '../services/api';
import { generateLessonPDF, generateSessionPDF } from '../utils/pdfGenerator';

function DailyLessonContent({ lesson, profile, sessionId, session, onBack, onComplete, progress }) {
  const [prayerChecks, setPrayerChecks] = useState(Array(lesson?.prayer_checklist?.length || 0).fill(false));
  const [closingPrayerChecks, setClosingPrayerChecks] = useState(Array(lesson?.closing_prayer?.length || 0).fill(false));
  const [activityAnswers, setActivityAnswers] = useState({});
  const [showActivityResults, setShowActivityResults] = useState(false);
  const [triviaAnswers, setTriviaAnswers] = useState({});
  const [triviaResults, setTriviaResults] = useState({});
  const [showTriviaResults, setShowTriviaResults] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [selectedKeyWords, setSelectedKeyWords] = useState([]);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [sessionLessons, setSessionLessons] = useState([]);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [randomizedActivities, setRandomizedActivities] = useState([]);
  const [awardingPoints, setAwardingPoints] = useState(false); // Prevent duplicate calls

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Randomize fill-in-the-blank activities on load
  useEffect(() => {
    const randomizeActivities = async () => {
      if (!lesson.activities || lesson.activities.length === 0) {
        setRandomizedActivities([]);
        return;
      }

      const randomized = await Promise.all(
        lesson.activities.map(async (activity) => {
          // If activity has is_randomized flag and is true, regenerate blanks
          if (activity.is_randomized && activity.verse_text && activity.num_words_hidden) {
            try {
              const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/ai/generate-fill-blank`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    verse_text: activity.verse_text,
                    verse_reference: activity.verse_number || lesson.reference,
                    num_words: activity.num_words_hidden
                  })
                }
              );
              
              if (!response.ok) {
                console.error('AI API returned error:', response.status);
                return activity; // Fall back to stored version
              }
              
              const data = await response.json();
              
              // Validate the response data
              if (!data.text || !data.answers) {
                console.error('AI API returned invalid data:', data);
                return activity; // Fall back to stored version
              }
              
              return {
                ...activity,
                activity_text: data.text,
                answers: data.answers
              };
            } catch (error) {
              console.error('Failed to randomize activity:', error);
              // Fall back to stored version (but ensure it has text)
              if (!activity.activity_text && !activity.text) {
                // If activity has no text at all, create a minimal valid structure
                return {
                  ...activity,
                  activity_text: "Error: Activity text missing",
                  answers: []
                };
              }
              return activity;
            }
          }
          // Return activity as-is if not randomized
          return activity;
        })
      );

      setRandomizedActivities(randomized);
    };

    randomizeActivities();
  }, [lesson.lesson_id]); // Re-randomize when lesson changes

  // Load session lessons for full session export
  useEffect(() => {
    const loadSessionLessons = async () => {
      try {
        const sessionIdTrimmed = (sessionId || '').trim();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/study-sessions/${sessionIdTrimmed}/lessons`,
          { credentials: 'include' }
        );
        const data = await response.json();
        setSessionLessons(data.lessons || []);
      } catch (error) {
        console.error('Error loading session lessons:', error);
      }
    };
    
    if (sessionId) {
      loadSessionLessons();
    }
  }, [sessionId]);

  // Return early if lesson is not loaded
  if (!lesson || !lesson.prayer_checklist || !lesson.closing_prayer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <p className="text-xl text-blue-700">Loading lesson...</p>
      </div>
    );
  }

  // Close tooltip when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close tooltip if clicking outside
      if (activeTooltip && !e.target.closest('[data-radix-tooltip-trigger]')) {
        setActiveTooltip(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeTooltip]);

  // Auto-save progress periodically
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (!lessonCompleted && !autoSaving) {
        autoSaveProgress();
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [prayerChecks, closingPrayerChecks, selectedKeyWords, earnedPoints]);

  const autoSaveProgress = async () => {
    try {
      setAutoSaving(true);
      
      // Save progress using session-based API
      const sessionIdTrimmed = (sessionId || '').trim();
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/progress/session/${profile.id}/${sessionIdTrimmed}/update`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            day_number: lesson.day_number,
            prayer_checklist: prayerChecks,
            closing_prayer_checklist: closingPrayerChecks,
            selected_key_words: selectedKeyWords
            // NOTE: Do NOT send earned_points here! They're already saved via awardPoints()
            // Sending them here causes duplication since backend uses $inc
          })
        }
      );
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  const handlePrayerCheck = (index) => {
    const newChecks = [...prayerChecks];
    newChecks[index] = !newChecks[index];
    setPrayerChecks(newChecks);
  };

  const handleClosingPrayerCheck = (index) => {
    const newChecks = [...closingPrayerChecks];
    newChecks[index] = !newChecks[index];
    setClosingPrayerChecks(newChecks);
  };

  const toggleKeyWord = (word) => {
    if (selectedKeyWords.includes(word)) {
      setSelectedKeyWords(selectedKeyWords.filter(w => w !== word));
    } else {
      setSelectedKeyWords([...selectedKeyWords, word]);
    }
  };

  const handleActivityInput = (activityIndex, blankIndex, value) => {
    setActivityAnswers({
      ...activityAnswers,
      [`${activityIndex}-${blankIndex}`]: value.trim().toLowerCase()
    });
  };

  // Function to award points immediately to profile when activity/trivia completed
  const awardPoints = async (points) => {
    if (points <= 0) return;
    
    // Prevent duplicate calls while already awarding points
    if (awardingPoints) {
      console.log(`[AWARD POINTS] Already in progress, skipping duplicate call`);
      return;
    }
    
    console.log(`[AWARD POINTS] Called with ${points} points for Day ${lesson.day_number}`);
    const callId = Date.now();
    console.log(`[AWARD POINTS ${callId}] Starting...`);
    
    setAwardingPoints(true);
    
    try {
      // Update total points on profile using atomic increment (prevents race conditions)
      console.log(`[AWARD POINTS ${callId}] Calling /add-points for profile...`);
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/profiles/${profile.id}/add-points?points=${points}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      console.log(`[AWARD POINTS ${callId}] Profile points updated`);
      
      // Update local profile object optimistically
      profile.totalPoints = (profile.totalPoints || 0) + points;
      
      // CRITICAL: Update day_progress earned_points for leaderboards
      // Backend uses $inc to accumulate, so we send the POINTS TO ADD (not new total)
      const sessionIdTrimmed = (sessionId || '').trim();
      
      console.log(`[AWARD POINTS ${callId}] Calling /update for day progress...`);
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/progress/session/${profile.id}/${sessionIdTrimmed}/update`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            day_number: lesson.day_number,
            earned_points: points  // Send points to ADD, backend uses $inc to accumulate
          })
        }
      );
      console.log(`[AWARD POINTS ${callId}] Day progress updated`);
      
      // Update local earned points tracker
      setEarnedPoints(prev => prev + points);
      console.log(`[AWARD POINTS ${callId}] Complete!`);
      
    } catch (error) {
      console.error('Failed to award points:', error);
    } finally {
      setAwardingPoints(false);
    }
  };

  // Normalize apostrophes and quotes for comparison
  const normalizeText = (text) => {
    return text
      .replace(/[\u2018\u2019]/g, "'")  // Replace curly single quotes with straight
      .replace(/[\u201C\u201D]/g, '"')  // Replace curly double quotes with straight
      .replace(/\u2013/g, '-')          // Replace en dash with hyphen
      .replace(/\u2014/g, '--');        // Replace em dash with double hyphen
  };

  const checkActivities = async () => {
    let correct = 0;
    let total = 0;
    
    // Use randomized activities if available, otherwise fall back to lesson activities
    const activitiesToCheck = randomizedActivities.length > 0 ? randomizedActivities : lesson.activities;
    
    activitiesToCheck.forEach((activity, activityIndex) => {
      activity.answers.forEach((answer, blankIndex) => {
        total++;
        const userAnswer = normalizeText((activityAnswers[`${activityIndex}-${blankIndex}`] || '').trim());
        const correctAnswer = normalizeText(answer.trim());
        if (userAnswer.toLowerCase() === correctAnswer.toLowerCase()) {
          correct++;
        }
      });
    });

    const points = Math.round((correct / total) * 50);
    
    // Award points immediately to profile (also updates earnedPoints state)
    await awardPoints(points);
    
    setShowActivityResults(true);
    toast({
      title: `Activity Complete! `,
      description: `You got ${correct} out of ${total} correct. +${points} points!`,
    });
  };

  const renderHyperlinkedMnemonic = (htmlString, baseIndex) => {
    // Parse HTML string and render with tooltips
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const elements = Array.from(doc.body.childNodes);
    
    return elements.map((node, idx) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return <span key={`${baseIndex}-text-${idx}`}>{node.textContent}</span>;
      }
      
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SPAN') {
        const targetWord = node.getAttribute('data-target');
        const mnemonicWord = node.textContent;
        const tooltipId = `${baseIndex}-link-${idx}`;
        const isOpen = activeTooltip === tooltipId;
        
        return (
          <Tooltip key={`${baseIndex}-tooltip-${idx}`} open={isOpen}>
            <TooltipTrigger 
              asChild
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTooltip(isOpen ? null : tooltipId);
              }}
              onPointerEnter={(e) => {
                if (e.pointerType === 'mouse') {
                  setActiveTooltip(tooltipId);
                }
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') {
                  setActiveTooltip(null);
                }
              }}
            >
              <span 
                className="cursor-pointer hover:bg-purple-200 active:bg-purple-300 hover:text-purple-900 px-1 rounded transition-colors select-none touch-manipulation underline decoration-dotted"
                style={{ 
                  WebkitTouchCallout: 'none', 
                  WebkitUserSelect: 'none', 
                  userSelect: 'none',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                {mnemonicWord}
              </span>
            </TooltipTrigger>
            <TooltipContent 
              className="bg-purple-700 text-white font-sans font-normal text-base"
              sideOffset={8}
            >
              <p>{targetWord}</p>
            </TooltipContent>
          </Tooltip>
        );
      }
      
      return null;
    }).filter(Boolean);
  };

  const findVerseStartIndex = (abbreviated, allWords) => {
    // Extract first few letters from the abbreviation to find where this verse starts
    const firstLetters = abbreviated.split('').filter(c => c.match(/[a-zA-Z]/)).slice(0, 5);
    
    // Try to find a sequence of words in scripture that match these first letters
    for (let startIdx = 0; startIdx < allWords.length - firstLetters.length; startIdx++) {
      let matches = true;
      for (let i = 0; i < firstLetters.length; i++) {
        const word = allWords[startIdx + i];
        if (!word || word[0].toLowerCase() !== firstLetters[i].toLowerCase()) {
          matches = false;
          break;
        }
      }
      if (matches) {
        return startIdx;
      }
    }
    
    return 0; // Default to start if not found
  };

  const renderMemorizationWithTooltips = (mem, index) => {
    // Support both old format (mem.abbreviated) and new format (mem.mnemonic)
    const mnemonicText = mem.mnemonic || mem.abbreviated;
    
    // If we have hyperlinked HTML from AI, render it with tooltip support
    if (mem.mnemonic_html) {
      return (
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
          <div 
            className="text-lg font-mono text-purple-800 mnemonic-hyperlinked"
            style={{ lineHeight: '2' }}
            spellCheck={false}
          >
            {/* Parse and render the HTML with tooltips */}
            {renderHyperlinkedMnemonic(mem.mnemonic_html, index)}
          </div>
        </TooltipProvider>
      );
    }
    
    // Use the FULL scripture passage or verse text
    const scriptureText = mem.verse_text || lesson.scripture;
    const allWords = scriptureText.split(/\s+/).map(word => word.replace(/[.,;:!?"()]/g, ''));
    
    // Find where this verse actually starts in the scripture
    const startWordIndex = findVerseStartIndex(mnemonicText, allWords);
    
    // Split the current abbreviated text into characters
    const abbreviatedChars = mnemonicText.split('');
    
    // Create a mapping of letters to words by matching sequentially
    let wordIndex = startWordIndex;
    const letterWordMap = [];
    
    for (let i = 0; i < abbreviatedChars.length; i++) {
      const char = abbreviatedChars[i];
      
      // Check if it's a space or punctuation - preserve as-is
      if (char.match(/\s/)) {
        letterWordMap.push({ char, word: null, isLetter: false, isPunctuation: false });
        continue;
      }
      
      if (char.match(/[.,;:!?"()]/)) {
        letterWordMap.push({ char, word: null, isLetter: false, isPunctuation: true });
        continue;
      }
      
      // It's a letter - get the corresponding word from scripture
      let foundWord = null;
      if (wordIndex < allWords.length) {
        foundWord = allWords[wordIndex];
        wordIndex++;
      }
      
      letterWordMap.push({ char, word: foundWord, isLetter: true, isPunctuation: false });
    }
    
    return (
      <TooltipProvider delayDuration={0} skipDelayDuration={0}>
        <div className="text-lg font-mono text-purple-800 flex flex-wrap" style={{ textTransform: 'none', lineHeight: '2' }} spellCheck={false}>
          {letterWordMap.map((item, idx) => {
            // Render spaces
            if (!item.isLetter && !item.isPunctuation && item.char.match(/\s/)) {
              return <span key={idx}> </span>;
            }
            
            // Render punctuation
            if (item.isPunctuation) {
              return <span key={idx} className="mr-1">{item.char}</span>;
            }
            
            // Render letters without tooltips if no word found
            if (item.isLetter && !item.word) {
              return <span key={idx} className="text-red-500" title="No match found">{item.char}</span>;
            }
            
            const tooltipId = `${index}-${idx}`;
            const isOpen = activeTooltip === tooltipId;
            
            // Render letters with tooltip (works on touch and hover)
            return (
              <Tooltip key={idx} open={isOpen}>
                <TooltipTrigger 
                  asChild
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveTooltip(isOpen ? null : tooltipId);
                  }}
                  onPointerEnter={(e) => {
                    // Only respond to mouse hover, not touch
                    if (e.pointerType === 'mouse') {
                      setActiveTooltip(tooltipId);
                    }
                  }}
                  onPointerLeave={(e) => {
                    // Only respond to mouse hover, not touch
                    if (e.pointerType === 'mouse') {
                      setActiveTooltip(null);
                    }
                  }}
                >
                  <span 
                    className="cursor-pointer hover:bg-purple-200 active:bg-purple-300 hover:text-purple-900 px-1 rounded transition-colors select-none touch-manipulation"
                    style={{ 
                      WebkitTouchCallout: 'none', 
                      WebkitUserSelect: 'none', 
                      userSelect: 'none',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    {item.char}
                  </span>
                </TooltipTrigger>
                <TooltipContent 
                  className="bg-purple-700 text-white font-sans font-normal text-base"
                  sideOffset={8}
                >
                  <p>{item.word}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    );
  };

  const renderActivityWithBlanks = (activity, activityIndex) => {
    // Support both old format (activity.text) and new format (activity.activity_text)
    const activityText = activity.activity_text || activity.text;
    
    // Safety check: if no text, return error message
    if (!activityText) {
      return (
        <div className="text-red-600 p-4 border border-red-300 rounded bg-red-50">
          Error: Activity text is missing. Please contact admin to fix this lesson.
        </div>
      );
    }
    
    const parts = activityText.split(/(_+)/);
    let blankIndex = 0;

    return (
      <div className="flex flex-wrap gap-2 items-center text-lg leading-relaxed">
        {parts.map((part, partIndex) => {
          if (part.match(/^_+$/)) {
            const currentBlankIndex = blankIndex++;
            const userAnswer = normalizeText((activityAnswers[`${activityIndex}-${currentBlankIndex}`] || '').trim());
            const correctAnswer = normalizeText((activity.answers?.[currentBlankIndex] || '').trim());
            const isCorrect = userAnswer.toLowerCase() === correctAnswer.toLowerCase();

            return (
              <span key={partIndex} className="inline-flex flex-col">
                <Input
                  className={`w-32 h-10 text-center ${
                    showActivityResults
                      ? isCorrect
                        ? 'border-green-500 bg-green-50'
                        : 'border-red-500 bg-red-50'
                      : 'border-blue-300'
                  }`}
                  onChange={(e) => handleActivityInput(activityIndex, currentBlankIndex, e.target.value)}
                  disabled={showActivityResults}
                  placeholder="?"
                />
                {showActivityResults && !isCorrect && (
                  <span className="text-xs text-green-600 mt-1 text-center font-semibold">{correctAnswer}</span>
                )}
              </span>
            );
          }
          return <span key={partIndex} className="text-blue-900">{part}</span>;
        })}
      </div>
    );
  };

  const checkTrivia = async () => {
    let points = 0;
    const results = {};
    
    Object.keys(triviaAnswers).forEach(key => {
      const questionIndex = parseInt(key);
      const userAnswer = normalizeText((triviaAnswers[key] || '').trim()).toLowerCase();
      const correctAnswer = normalizeText((lesson.trivia?.[questionIndex]?.answer || '').trim()).toLowerCase();
      
      // Check if answer is correct (case-insensitive, flexible matching)
      // Accept answers that contain the key parts of the correct answer
      const isCorrect = userAnswer && (
        userAnswer === correctAnswer ||
        correctAnswer.includes(userAnswer) ||
        userAnswer.includes(correctAnswer) ||
        // Check for number equivalence (e.g., "7" === "seven")
        checkNumberEquivalence(userAnswer, correctAnswer) ||
        // Check for partial word matches (at least 70% of words match)
        checkPartialMatch(userAnswer, correctAnswer)
      );
      
      results[questionIndex] = isCorrect;
      
      if (isCorrect) {
        points += lesson.trivia[questionIndex].points;
      }
    });
    
    setTriviaResults(results);
    
    // Award points immediately to profile (also updates earnedPoints state)
    await awardPoints(points);
    
    setShowTriviaResults(true);
    
    const totalQuestions = Object.keys(triviaAnswers).length;
    const correctCount = Object.values(results).filter(r => r).length;
    
    toast({
      title: 'Trivia Submitted!',
      description: `You got ${correctCount} out of ${totalQuestions} correct. +${points} points!`,
    });
  };
  
  // Helper function to check number equivalence
  const checkNumberEquivalence = (userAnswer, correctAnswer) => {
    const numberWords = {
      'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4',
      'five': '5', 'six': '6', 'seven': '7', 'eight': '8', 'nine': '9',
      'ten': '10', 'eleven': '11', 'twelve': '12', 'thirteen': '13',
      'fourteen': '14', 'fifteen': '15', 'sixteen': '16', 'seventeen': '17',
      'eighteen': '18', 'nineteen': '19', 'twenty': '20', 'thirty': '30',
      'forty': '40', 'fifty': '50', 'sixty': '60', 'seventy': '70',
      'eighty': '80', 'ninety': '90', 'hundred': '100', 'thousand': '1000'
    };
    
    // Convert both answers to normalized forms
    const normalizeNumber = (str) => {
      // If it's already a number, return it
      if (/^\d+$/.test(str.trim())) return str.trim();
      
      // Check if it contains a number word
      for (const [word, num] of Object.entries(numberWords)) {
        if (str.includes(word)) {
          return num;
        }
      }
      return str;
    };
    
    const userNormalized = normalizeNumber(userAnswer);
    const correctNormalized = normalizeNumber(correctAnswer);
    
    // Check if they match after normalization
    return userNormalized === correctNormalized || 
           userAnswer === correctNormalized ||
           userNormalized === correctAnswer;
  };
  
  // Helper function to check partial matches
  const checkPartialMatch = (userAnswer, correctAnswer) => {
    // Filter out common filler words that shouldn't be required for correctness
    const fillerWords = ['the', 'and', 'or', 'a', 'an', 'of', 'in', 'to', 'for', 'is', 'it', 'as', 'be', 'by'];
    
    const userWords = userAnswer.split(/\s+/)
      .filter(w => w.length > 2)
      .filter(w => !fillerWords.includes(w));
    
    const correctWords = correctAnswer.split(/\s+/)
      .filter(w => w.length > 2)
      .filter(w => !fillerWords.includes(w));
    
    if (userWords.length === 0 || correctWords.length === 0) return false;
    
    // Count how many correct words are found in user's answer
    let correctWordsFound = 0;
    correctWords.forEach(correctWord => {
      if (userWords.some(userWord => 
        correctWord.includes(userWord) || userWord.includes(correctWord)
      )) {
        correctWordsFound++;
      }
    });
    
    // Accept if user has ALL the required words (even with extras)
    // OR if at least 80% of the required words are present
    return (correctWordsFound / correctWords.length) >= 0.80;
  };

  const handleCompleteLesson = async () => {
    setLessonCompleted(true);
    
    // Save final progress before completing
    await autoSaveProgress();
    
    toast({
      title: '🎉 Lesson Complete!',
      description: `Great job! You earned ${earnedPoints} points today.`,
    });
    
    // Wait a moment then call onComplete
    setTimeout(() => {
      onComplete(lesson.day_number, earnedPoints);
    }, 2000);
  };

  const handleExportLesson = async () => {
    try {
      setLoadingPDF(true);
      // Use randomized activities if available, otherwise use original
      const lessonForPDF = {
        ...lesson,
        activities: randomizedActivities.length > 0 ? randomizedActivities : lesson.activities
      };
      await generateLessonPDF(lessonForPDF, session, profile.name);
      toast({
        title: 'PDF Generated!',
        description: 'Your lesson has been exported successfully.',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoadingPDF(false);
    }
  };

  const handleExportSession = async () => {
    try {
      setLoadingPDF(true);
      if (sessionLessons.length === 0) {
        toast({
          title: 'Loading...',
          description: 'Please wait while we load all lessons.',
        });
        return;
      }
      await generateSessionPDF(sessionLessons, session, profile.name);
      toast({
        title: 'PDF Generated!',
        description: 'Your complete session has been exported successfully.',
      });
    } catch (error) {
      console.error('Error generating session PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate session PDF. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoadingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="bg-white border-b border-blue-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={onBack} className="text-blue-700 hover:text-blue-900">
              <ArrowLeft className="mr-2" /> Back to Dashboard
            </Button>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-sm text-blue-700">Day {lesson.day_number}</p>
                <p className="font-bold text-blue-900">{lesson.title}</p>
              </div>
              <div className="bg-green-100 px-4 py-2 rounded-lg">
                <p className="text-xs text-green-700">Points Today</p>
                <p className="font-bold text-green-900">⭐ +{earnedPoints}</p>
              </div>
              {autoSaving && (
                <p className="text-xs text-blue-600">Saving...</p>
              )}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleExportLesson}
                  disabled={loadingPDF}
                  className="text-blue-700 border-blue-300 hover:bg-blue-50"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  {loadingPDF ? 'Generating...' : 'Print Lesson'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleExportSession}
                  disabled={loadingPDF || sessionLessons.length === 0}
                  className="text-purple-700 border-purple-300 hover:bg-purple-50"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  {loadingPDF ? 'Generating...' : 'Print Full Session'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="prayer" className="space-y-6">
          <div className="sticky top-[73px] z-10 bg-gradient-to-br from-blue-50 via-white to-green-50 pb-4">
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 gap-1 bg-blue-100 shadow-md h-auto p-1">
              <TabsTrigger value="prayer" className="data-[state=active]:bg-white whitespace-normal h-auto py-2 px-2 text-sm">Prayer Time</TabsTrigger>
              <TabsTrigger value="scripture" className="data-[state=active]:bg-white whitespace-normal h-auto py-2 px-2 text-sm">Meditate</TabsTrigger>
              <TabsTrigger value="memorize" className="data-[state=active]:bg-white whitespace-normal h-auto py-2 px-2 text-sm">Memorize</TabsTrigger>
              <TabsTrigger value="activities" className="data-[state=active]:bg-white whitespace-normal h-auto py-2 px-2 text-sm">Activities</TabsTrigger>
              <TabsTrigger value="trivia" className="data-[state=active]:bg-white whitespace-normal h-auto py-2 px-2 text-sm">Trivia</TabsTrigger>
              <TabsTrigger value="closing" className="data-[state=active]:bg-white whitespace-normal h-auto py-2 px-2 text-sm">Closing Prayer</TabsTrigger>
            </TabsList>
          </div>

          {/* Prayer Time Tab */}
          <TabsContent value="prayer">
            <Card className="border-2 border-blue-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-blue-900">🙏 Daily Prayer Time</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {lesson.prayer_checklist.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                    <Checkbox
                      checked={prayerChecks[index]}
                      onCheckedChange={() => handlePrayerCheck(index)}
                      className="border-blue-400"
                    />
                    <label className="text-lg text-blue-900 cursor-pointer flex-1">{item}</label>
                    {prayerChecks[index] && <CheckCircle2 className="text-green-600" />}
                  </div>
                ))}
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">✅ {prayerChecks.filter(Boolean).length} of {prayerChecks.length} completed</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meditate Tab */}
          <TabsContent value="scripture">
            <Card className="border-2 border-blue-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-blue-900">🧘 Meditate on God's Word</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Meditation Instructions */}
                <div className="bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500 mb-6">
                  <p className="text-purple-900 font-semibold mb-3">📚 How to Meditate:</p>
                  <p className="text-purple-800 italic leading-relaxed">
                    Take plenty of time to read slowly, reread emphatically, and highlight words that help you to keep the words hidden in your heart. 
                    A healthy meditation of a passage should be read and thought upon <strong>five to eight times</strong>. 
                    If done properly you'll complete the following activities more easily. Think on these things!
                  </p>
                </div>

                {/* Scripture Text */}
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500 mb-6">
                  <p className="text-sm font-semibold text-blue-700 mb-3">{lesson.reference}</p>
                  <p className="text-lg leading-relaxed text-blue-900 font-serif">{lesson.scripture}</p>
                  <p className="text-right mt-4 font-semibold text-blue-700">— {lesson.reference}</p>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold text-blue-900 flex items-center">
                    <Lightbulb className="mr-2 text-amber-500" /> Pick out the key words from the passage:
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">Click on words in the scripture below to mark them as key words</p>
                  <div className="bg-white p-6 rounded-lg border-2 border-blue-300 mb-4">
                    <div className="flex flex-wrap gap-2 text-lg leading-relaxed">
                      {lesson.scripture.split(/\s+/).map((word, index) => {
                        const cleanWord = word.replace(/[.,;:!?"]/g, '');
                        const isSelected = selectedKeyWords.includes(cleanWord);
                        const isCorrectKeyWord = lesson.key_words.map(k => k.toLowerCase()).includes(cleanWord.toLowerCase());
                        
                        return (
                          <button
                            key={index}
                            onClick={() => toggleKeyWord(cleanWord)}
                            className={`px-2 py-1 rounded transition-all ${
                              isSelected
                                ? 'bg-amber-200 text-amber-900 font-semibold border-2 border-amber-400'
                                : 'hover:bg-blue-100 text-blue-900'
                            }`}
                          >
                            {word}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-blue-900">Your selected key words:</h4>
                    <div className="flex flex-wrap gap-2 min-h-[60px] bg-blue-50 p-4 rounded-lg border border-blue-200">
                      {selectedKeyWords.length === 0 ? (
                        <p className="text-blue-600 italic">Click words above to select them</p>
                      ) : (
                        selectedKeyWords.map((word, index) => (
                          <span key={index} className="bg-amber-200 text-amber-900 px-4 py-2 rounded-full font-medium border border-amber-400">
                            {word}
                          </span>
                        ))
                      )}
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-2">Suggested key words (for reference):</h4>
                      <div className="flex flex-wrap gap-2">
                        {lesson.key_words.map((word, index) => (
                          <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm border border-green-300">
                            {word}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Memorization Tab */}
          <TabsContent value="memorize">
            <Card className="border-2 border-purple-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-purple-900">🧠 Memorize the Word</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-purple-800 mb-6">
                  Use these abbreviated verses to help memorize. <span className="font-semibold">Hover over or tap any letter to see the full word!</span>
                </p>
                <div className="space-y-4">
                  {lesson.memorization.map((mem, index) => (
                    <div key={index} className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <p className="font-semibold text-purple-900 mb-2">
                        {mem.verse_number || `Verse ${mem.verse}`}:
                      </p>
                      {renderMemorizationWithTooltips(mem, index)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities">
            <Card className="border-2 border-green-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-green-900">✏️ Fill in the Blanks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {(randomizedActivities.length > 0 ? randomizedActivities : lesson.activities).map((activity, activityIndex) => (
                  <div key={activityIndex} className="bg-green-50 p-6 rounded-lg border border-green-200">
                    <p className="font-semibold text-green-900 mb-4">
                      {activity.verse_number || `Verse ${activity.verse}`}:
                    </p>
                    {renderActivityWithBlanks(activity, activityIndex)}
                  </div>
                ))}
                {!showActivityResults ? (
                  <Button
                    onClick={checkActivities}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                  >
                    Check My Answers
                  </Button>
                ) : (
                  <div className="bg-green-100 p-6 rounded-lg border border-green-300 text-center">
                    <CheckCircle2 className="mx-auto mb-2 text-green-600" size={48} />
                    <p className="text-lg font-semibold text-green-900">Activity Completed!</p>
                    <p className="text-green-800">Great job! Review the corrections above.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trivia Tab */}
          <TabsContent value="trivia">
            <Card className="border-2 border-amber-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-amber-900">🎯 Bible Trivia</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {lesson.trivia.map((question, index) => {
                  const isAnswered = triviaAnswers[index]?.trim();
                  const isCorrect = triviaResults[index];
                  const isIncorrect = showTriviaResults && isAnswered && !isCorrect;
                  
                  return (
                    <div key={index} className={`p-6 rounded-lg border-2 ${
                      showTriviaResults 
                        ? isCorrect 
                          ? 'bg-green-50 border-green-300' 
                          : isIncorrect
                          ? 'bg-red-50 border-red-300'
                          : 'bg-gray-50 border-gray-300'
                        : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex justify-between items-start mb-3">
                        <p className="font-semibold text-amber-900 flex-1">{question.question}</p>
                        <div className="flex items-center gap-2">
                          <span className="bg-amber-200 text-amber-900 px-3 py-1 rounded-full text-sm font-bold">
                            {question.points} pts
                          </span>
                          {showTriviaResults && isCorrect && (
                            <CheckCircle2 className="text-green-600" size={24} />
                          )}
                          {showTriviaResults && isIncorrect && (
                            <XCircle className="text-red-600" size={24} />
                          )}
                        </div>
                      </div>
                      {question.reference && (
                        <p className="text-sm text-amber-700 mb-3">Reference: {question.reference}</p>
                      )}
                      <Textarea
                        placeholder="Your answer..."
                        value={triviaAnswers[index] || ''}
                        rows={3}
                        className={`${
                          showTriviaResults
                            ? isCorrect
                              ? 'border-green-500 bg-green-50'
                              : isIncorrect
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-300'
                            : 'border-amber-300'
                        }`}
                        onChange={(e) => setTriviaAnswers({...triviaAnswers, [index]: e.target.value})}
                        onPaste={(e) => e.preventDefault()}
                        disabled={showTriviaResults}
                      />
                      {showTriviaResults && (
                        <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-300">
                          <p className="text-sm font-semibold text-blue-900">Correct Answer: {question.answer}</p>
                          {isIncorrect && (
                            <p className="text-xs text-red-700 mt-1">Your answer was not close enough to the correct answer.</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!showTriviaResults ? (
                  <Button
                    onClick={checkTrivia}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg"
                  >
                    Submit Trivia
                  </Button>
                ) : (
                  <div className="bg-amber-100 p-6 rounded-lg border border-amber-300 text-center">
                    <CheckCircle2 className="mx-auto mb-2 text-amber-600" size={48} />
                    <p className="text-lg font-semibold text-amber-900">Trivia Completed!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Closing Prayer Tab */}
          <TabsContent value="closing">
            <Card className="border-2 border-purple-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-purple-900">🙏 Closing Prayer Time</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-purple-800 mb-4">Take time to reflect and respond to what God has taught you today:</p>
                {lesson.closing_prayer.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                    <Checkbox
                      checked={closingPrayerChecks[index]}
                      onCheckedChange={() => handleClosingPrayerCheck(index)}
                      className="border-purple-400"
                    />
                    <label className="text-lg text-purple-900 cursor-pointer flex-1">{item}</label>
                    {closingPrayerChecks[index] && <CheckCircle2 className="text-purple-600" />}
                  </div>
                ))}
                <div className="mt-6 p-4 bg-purple-100 border border-purple-300 rounded-lg">
                  <p className="text-purple-800 font-medium">✓ {closingPrayerChecks.filter(Boolean).length} of {closingPrayerChecks.length} completed</p>
                </div>

                {/* Complete Lesson Button - Only shown in Closing Prayer tab */}
                {!lessonCompleted ? (
                  <div className="mt-8 border-2 border-green-300 bg-gradient-to-r from-green-50 to-blue-50 shadow-lg rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-green-900 mb-2">Ready to finish?</h3>
                        <p className="text-green-800">Complete all sections above, then mark this lesson as complete to move forward!</p>
                        <p className="text-sm text-green-700 mt-2">Total points earned today: <span className="font-bold text-green-900">⭐ {earnedPoints}</span></p>
                      </div>
                      <Button
                        onClick={handleCompleteLesson}
                        className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg ml-4"
                      >
                        ✓ Complete Lesson
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 border-2 border-green-400 bg-green-50 shadow-lg rounded-lg p-8 text-center">
                    <CheckCircle2 className="mx-auto mb-4 text-green-600" size={64} />
                    <h3 className="text-2xl font-bold text-green-900 mb-2">🎉 Lesson Complete!</h3>
                    <p className="text-lg text-green-800">Excellent work! You earned {earnedPoints} points today.</p>
                    <p className="text-green-700 mt-2">Returning to dashboard...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default function DailyLesson(props) {
  // Wrapper component to handle loading state
  if (!props.lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <p className="text-xl text-blue-700">Loading lesson...</p>
      </div>
    );
  }
  
  // Check if all required arrays exist
  const requiredFields = [
    'prayer_checklist',
    'closing_prayer',
    'key_words',
    'memorization',
    'activities',
    'trivia'
  ];
  
  const missingFields = requiredFields.filter(field => !Array.isArray(props.lesson[field]));
  
  if (missingFields.length > 0) {
    console.error('Missing or invalid arrays in lesson:', missingFields, props.lesson);
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-700">Error: Incomplete lesson data</p>
          <p className="text-sm text-red-600 mt-2">Missing fields: {missingFields.join(', ')}</p>
          <Button onClick={props.onBack} className="mt-4 bg-blue-600 hover:bg-blue-700">Go Back</Button>
        </div>
      </div>
    );
  }
  
  return <DailyLessonContent {...props} />;
}

